import { EventType, UserEventStatus } from "../../../../common/enums";
import { isReservationCancelled } from "../../../../common/infrastructure/utilities/reservationUtility";
import { ICalendarEventModel } from "../models/calendarEventModel";
export interface EventDetailsContext {
  isPassed?: boolean;
  eventType?: EventType;
  hasEveryoneReserved?: boolean;
  areReservationsClosed?: boolean;
  canCancelReservations?: boolean;
  isCancellationWindowClosed?: boolean;
  allowLateCancellationsWithPayment?: boolean;
  isPaid?: boolean;
  status?: UserEventStatus;
}

export function getEventDetailsContentWarningMessage(
  context: EventDetailsContext,
): string | undefined {
  const {
    isPassed,
    eventType,
    hasEveryoneReserved,
    areReservationsClosed,
    canCancelReservations,
    isCancellationWindowClosed,
    allowLateCancellationsWithPayment,
    isPaid,
    status,
  } = context;

  if (!!isPassed) {
    return;
  }

  // Only show warning messages if reservation is in Confirmed status
  if (status !== undefined && status !== UserEventStatus.Confirmed) {
    return;
  }

  if (!!eventType && eventType === EventType.Individual) {
    return "Не можете да отмените индивидуален клас. Моля, свържете се с обекта за съдействие.";
  }

  if (!!areReservationsClosed && !hasEveryoneReserved) {
    return "Резервациите за този клас през системата вече са затворени. Моля, свържете се с обекта за съдействие..";
  }

  if (canCancelReservations !== undefined && !canCancelReservations) {
    return "Тази резервация не може да бъде канселирана. Моля, свържете се с обекта за съдействие.";
  }

  // A paid reservation is the studio's to cancel; a payment method alone
  // (e.g. Multisport) still lets the client cancel it.
  if (!!canCancelReservations && !!isPaid) {
    return "Тази резервация вече е платена и не може да бъде отказана. Моля, свържете се с обекта за съдействие.";
  }

  if (
    isCancellationWindowClosed &&
    canCancelReservations &&
    allowLateCancellationsWithPayment
  ) {
    return "Прозорцът за канселиране е затворен. Можете да канселирате резервацията, но ще трябва да я заплатите.";
  }

  return;
}

export const getEventRegistrationStatusText = (
  status: UserEventStatus,
  isCurrentUser: boolean,
  isCancelled: boolean,
  type: EventType,
  userName?: string,
  isPassed?: boolean,
): string => {
  const name = userName ?? "";
  const showClassTexts =
    type === EventType.Individual || type === EventType.Group;

  if (isReservationCancelled(isCancelled, status)) {
    return isCurrentUser
      ? "Резервацията Ви е била отказана."
      : `Резервацията на ${name} е била отказана.`;
  }

  switch (status) {
    case UserEventStatus.Pending:
      // Show "Unconfirmed" text for Pending reservations when event has passed
      if (isPassed) {
        return isCurrentUser
          ? "Непотвърдена резервация."
          : `${name} има непотвърдена резервация.`;
      }
      return isCurrentUser
        ? "Очаква се потвърждение на резервацията Ви."
        : `Очаква се потвърждение на резервацията на ${name}.`;

    case UserEventStatus.Confirmed:
      if (showClassTexts) {
        return isCurrentUser
          ? "Вие сте записан/а за този клас."
          : `${name} е записан/а за този клас.`;
      } else {
        return isCurrentUser
          ? "Резервацията Ви е потвърдена."
          : `Резервацията на ${name} в потвърдена.`;
      }

    case UserEventStatus.WaitingList:
      return isCurrentUser
        ? "Вие сте записани в изчакващи за този клас. Ако се освободи място, ще получите имейл с линк за запазване на мястото. Не забравяйте, че някой друг може да заеме мястото преди Вас."
        : `${name} е записан/а в изчакващи за този клас. Ако се освободи място, ще получи имейл с линк за запазване на мястото. Не забравяйте, че някой друг може да заеме мястото преди Вас.`;

    case UserEventStatus.Arrived:
      // If event hasn't passed yet, treat as confirmed instead of arrived
      if (!isPassed) {
        if (showClassTexts) {
          return isCurrentUser
            ? "Вие сте записан/а за този клас."
            : `${name} е записан/а за този клас.`;
        } else {
          return isCurrentUser
            ? "Резервацията Ви е потвърдена."
            : `Резервацията на ${name} в потвърдена.`;
        }
      }

      if (showClassTexts) {
        return isCurrentUser
          ? "Вие сте присъствали на този клас."
          : `${name} е присъствал/а на този клас.`;
      } else {
        return isCurrentUser
          ? "Тази резервация вече е била взета."
          : `Тази резервация вече е била взета от ${name}.`;
      }

    case UserEventStatus.Missed:
      if (showClassTexts) {
        return isCurrentUser ? "Изпуснат клас." : `${name} е изпуснал/а класа.`;
      } else {
        return isCurrentUser
          ? "Изпусната резервация."
          : `${name} е изпуснал/а резервацията.`;
      }

    case UserEventStatus.OnlyPaid:
      if (showClassTexts) {
        return isCurrentUser
          ? "Изпуснат, но платен клас."
          : `${name} е изпуснал/а, но платил класа.`;
      } else {
        return isCurrentUser
          ? "Изпусната, но платена резервация."
          : `${name} е изпуснал/а, но платил резервацията.`;
      }
    default:
      return "";
  }
};

/** How a reservation status reads at a glance: booked, still waiting, or off. */
export type ReservationTone = "booked" | "waiting" | "cancelled";

export const getReservationTone = (
  status: UserEventStatus,
  isCancelled: boolean,
): ReservationTone => {
  if (
    isReservationCancelled(isCancelled, status) ||
    status === UserEventStatus.Missed
  ) {
    return "cancelled";
  }

  if (
    status === UserEventStatus.Pending ||
    status === UserEventStatus.WaitingList
  ) {
    return "waiting";
  }

  return "booked";
};

export const buildRequirementsMessage = (
  requiresPrepayment?: boolean,
  requiresFormCompletion?: boolean,
): string | null => {
  if (!requiresPrepayment && !requiresFormCompletion) {
    return null;
  }

  if (requiresPrepayment && requiresFormCompletion) {
    return `
      <p><b>Този клас изисква предварително плащане и попълване на формуляр.</b></p>
      <p>След като направите резервация, ще получите имейл с линк за плащане и формуляр, които трябва да попълните преди класа.</p>
      <p>Резервацията Ви ще бъде потвърдена след получаване на плащането.</p>
    `;
  }

  if (requiresPrepayment) {
    return `
      <p><b>Този клас изисква предварително плащане.</b></p>
      <p>След като направите резервация, ще получите имейл с линк за плащане.</p>
      <p>Резервацията Ви ще бъде потвърдена след получаване на плащането.</p>
    `;
  }

  if (requiresFormCompletion) {
    return `
      <p><b>Този клас изисква попълване на формуляр.</b></p>
      <p>След като направите резервация, ще получите имейл с линк към формуляра, който трябва да попълните преди класа.</p>
    `;
  }

  return null;
};

export const getAvailableSpacesLabel = (item?: ICalendarEventModel) => {
  if (!item) {
    return null;
  }

  // No spaces or waitlist only
  if (item?.leftSpaces === 0 || !item?.hasAvailableSpaces) {
    return "Има места в изчакващи";
  }

  // Singular form
  if (item?.leftSpaces === 1) {
    return "1 свободно място";
  }

  // Plural form with counter
  if (item?.leftSpaces) {
    return `${item.leftSpaces} свободни места`;
  }

  // Fallback - no counter available
  return "Има места";
};
